// import { useMemo } from 'react';
import { GeoJsonLayer, ScatterplotLayer, TextLayer } from '@deck.gl/layers';
import {DataFilterExtension} from '@deck.gl/extensions';
// import {CollisionFilterExtension} from '@deck.gl/extensions';
import { sensorToColor } from '../utils/layer_style';
import { useOGCCollection } from '../utils/ogc';
import type { PickingInfo } from '@deck.gl/core';
import type { GeoJsonFeature } from '../utils/ogc';
import { useMemo } from 'react';
import { transformEventWithDepthData } from './event_with_depth_data';
import type { PastFloodEventFeature } from './past_flood_events';
import { OGC_COLLECTIONS } from '../config';


type SensorLocationFeatureProperties = {
  deployment_id: string;
  name: string;
  neighborhood: string;
  borough: string;
  fk_status: string;
  date_deployed: Date;
  date_down: Date | null;
  flood_event_count:	number;
  sensor_mount:  string;
  mounted_over:  string;
  max_depth_inches_of_any_flood:	number;
  max_depth_inches_of_last_flood: number;
  last_flood_time: Date | null;	
  last_flood_end_time: Date | null;
  last_depth_inches: number | null;
  last_measurement_time: Date | null;

  // TODO: add
  // Precipitation Data Source
  // Tidal Data Source
};

export type SensorLocationFeature = GeoJsonFeature & {
  properties: SensorLocationFeatureProperties;
};

export type AugmentedSensorFeature = SensorLocationFeature & {
  properties: SensorLocationFeature['properties'] & {
    max_depth_inches: number;
    current_depth_inches?: number;
    depth_inches: number[];
    time: Date[];
    past_events: PastFloodEventFeature[];
  };
};


export const transformSensorLocation = (d: GeoJsonFeature) => {
    d.id = d.id || d.properties.id || d.properties.deployment_id;
    // console.log(d)    
    d.properties.date_deployed = new Date(d.properties.date_deployed);
    d.properties.date_down = d.properties.date_down ? new Date(d.properties.date_down) : null;
    d.properties.last_flood_time = d.properties.last_flood_time ? new Date(d.properties.last_flood_time) : null;
    d.properties.last_flood_end_time = d.properties.last_flood_end_time ? new Date(d.properties.last_flood_end_time) : null;
    d.properties.last_measurement_time = d.properties.last_measurement_time ? new Date(d.properties.last_measurement_time) : null;
    if (d.properties.fk_status === 'visible' && (!d.properties.last_measurement_time || d.properties.last_measurement_time.getTime() < new Date().getTime() - 2 * 60 * 60 * 1000)) {
      d.properties.fk_status = 'offline';
    }
    return d as SensorLocationFeature;
}

const sortLastMeasurementTime = (a: SensorLocationFeature, b: SensorLocationFeature) => {
  const at = a.properties.last_measurement_time ? a.properties.last_measurement_time.getTime() : 0;
  const bt = b.properties.last_measurement_time ? b.properties.last_measurement_time.getTime() : 0;
  return -(bt - at);
};

export const useSensorLocations = () => {
  const data = useOGCCollection(
    OGC_COLLECTIONS.SENSOR_LOCATIONS,
    { limit: 1000 },
    undefined,
    { refreshInterval: 5 * 60 * 1000 }
  );
  data.features = useMemo(() => data.features?.map(transformSensorLocation).sort(sortLastMeasurementTime), [data.features]);
  return data as { features: SensorLocationFeature[] | undefined; loading: boolean; error: any };
};

export default function useSensorLocationLayers(features: SensorLocationFeature[] | undefined, { currentTime, timeRange, onClick, offlineHidden }: { currentTime?: Date; timeRange: [Date | undefined, Date | undefined]; onClick?: (info: PickingInfo<GeoJsonFeature>) => void, offlineHidden?: boolean }) {
  // const { features } = useSensorLocations();
  const [fromTime, toTime] = timeRange;

  return (
    !features ? [] : 
    [
      new GeoJsonLayer({
      id: 'sensor_locations',
      data: { type: 'FeatureCollection', features },
      // type: 'mvt',
      // data: ['https://gis.dev.floodlabs.nyc/tiles/ogc.sensor_locations/{z}/{x}/{y}.pbf'],

      // pointRadiusUnits: 'pixels' as const,
      pointRadiusUnits: 'meters' as const,
      pointLineWidthUnits: 'pixels' as const,
      stroked: true,
      filled: true,
      pickable: true,
      autoHighlight: false,
      // getLineColor: [0, 120, 200, 200],
      
      minZoom: 12,
      // maxZoom: 15,
      getFillColor: (f: GeoJsonFeature) => sensorToColor(f),
      // getLineColor: [255, 255, 255, 200],
      // getLineColor: [120, 120, 160, 200],
      // getLineColor: [200, 200, 240, 200],
      getLineColor: [120, 120, 160, 100],
      lineWidthMinPixels: 0.7,
      // lineWidthMinPixels: 0.5,
      getLineWidth: 0,
      // getPointRadius: 2,
      getPointRadius: 30,
      pointRadiusMinPixels: 4,
      pointRadiusMaxPixels: 12,
      // pointR
      parameters: { depthTest: false },
      onClick,
      

      getFilterValue: (d: GeoJsonFeature) => {
        return (
          currentTime ? 
            d.properties.date_deployed <= currentTime &&
            (!d.properties.date_down || currentTime <= d.properties.date_down)
            : 
            (!d.properties.date_deployed || !toTime || d.properties.date_deployed <= toTime) &&
            (!d.properties.date_down || !fromTime || d.properties.date_down >= fromTime)
        ) &&
        d.properties.fk_status !== 'hidden' &&
        !(offlineHidden && (d.properties.fk_status === 'offline' || d.properties.fk_status === 'retired'))
        ? 1 : -1
      },
      filterRange: [0, 1],
      extensions: [
        new DataFilterExtension({ filterSize: 1 })
      ],
    })
  ])
}
